import { TextButton } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import { Location, LocationDescriptor } from 'history';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T, typographyCss } from 'common/typography';
import { RowType, SummaryTableRow } from 'modules/insights-v2/types';

import InsightMenuRow from './InsightMenuRow';
import InsightMetricRow from './InsightMetricRow';
import LoadingView from '../LoadingView';
import SectionHeader from '../SectionHeader';

type SummaryTableCommonProps = {
  title: string;
  tooltip?: string;
  link?: JSX.Element | string | ((location: Location) => LocationDescriptor);
  isLinkVisible?: boolean;
  className?: string;
  isError: boolean;
  onRetry: (() => void) | (() => Promise<void>);
  isFetching: boolean;
};

type SummaryTableTemplateProps = SummaryTableCommonProps & {
  items: {
    content: React.ReactNode;
    key: string;
    type?: RowType;
  }[];
};

type SummaryTableProps = SummaryTableCommonProps & {
  rows: SummaryTableRow[];
};

type SummaryCardProps = SummaryTableCommonProps & {
  children: React.ReactNode | React.ReactNode[];
};

const TableWrapper = styled.div`
  border: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  border-radius: 8px;
  width: 100%;

  flex: 1 1 auto;

  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const TableHeader = styled.div`
  border-block-end: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  padding: 16px;
  display: flex;
  justify-content: space-between;
`;

const TableRow = styled.div<{ $raw?: boolean }>`
  border-block-end: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  &:last-child {
    border-block-end: none;
  }
  min-height: 56px;

  display: flex;
  flex-direction: column;
  justify-content: center;

  flex: 1 1 auto;

  padding-inline: ${props => (props.$raw ? 0 : '16px')};
`;

const SeeAllLink = styled(Link).attrs({ fontWeight: 'semiBold' })`
  ${typographyCss.Body2};
`;

const PlaceholderRoot = styled.div`
  display: flex;
  justify-content: center;
  min-height: 280px;
  height: 100%;
`;

const PlaceholderText = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const TableContent = ({
  isFetching,
  onRetry,
  isError,
  items,
}: {
  isFetching: boolean;
  onRetry: () => void;
  isError: boolean;
  items: { content: React.ReactNode; key: string; type?: RowType }[];
}) => {
  if (isFetching) {
    return (
      <PlaceholderRoot>
        <LoadingView />
      </PlaceholderRoot>
    );
  } else if (isError) {
    return (
      <PlaceholderRoot>
        <Empty
          image={null}
          description={
            <PlaceholderText>
              <FormattedMessage id="insights.error-view.description" />
            </PlaceholderText>
          }
          footer={
            <TextButton onClick={onRetry}>
              <FormattedMessage id="insights.error-view.try-again" />
            </TextButton>
          }
        />
      </PlaceholderRoot>
    );
  } else if (!items?.length) {
    return (
      <PlaceholderRoot>
        <Empty
          description={
            <PlaceholderText>
              <FormattedMessage id="insights.no-data" />
            </PlaceholderText>
          }
        />
      </PlaceholderRoot>
    );
  } else {
    return (
      <>
        {items?.map(({ content, key, type }) => (
          <TableRow key={key} $raw={type === RowType.Raw}>
            {content}
          </TableRow>
        ))}
      </>
    );
  }
};

const SummaryTableTemplate = ({
  items,
  link,
  isLinkVisible,
  title,
  tooltip,
  className,
  isError,
  isFetching,
  onRetry,
}: SummaryTableTemplateProps) => {
  return (
    <TableWrapper className={className}>
      <TableHeader>
        <SectionHeader title={title} tooltip={tooltip} />
        {(isLinkVisible ?? (!!items?.length && !isError && !isFetching)) &&
          (typeof link === 'string' || typeof link === 'function' ? (
            <SeeAllLink to={link}>
              <FormattedMessage id="insights.see-all" />
            </SeeAllLink>
          ) : (
            link
          ))}
      </TableHeader>
      <TableContent isFetching={isFetching} onRetry={onRetry} isError={isError} items={items} />
    </TableWrapper>
  );
};

const SummaryTable = ({
  link,
  className,
  isLinkVisible,
  rows,
  title,
  tooltip,
  isError,
  isFetching,
  onRetry,
}: SummaryTableProps) => (
  <SummaryTableTemplate
    title={title}
    tooltip={tooltip}
    link={link}
    className={className}
    isLinkVisible={isLinkVisible}
    isError={isError}
    onRetry={onRetry}
    isFetching={isFetching}
    items={rows.map(item => {
      if (item.type === RowType.Metric) {
        return { content: <InsightMetricRow data={item} />, key: item.title };
      } else if (item.type === RowType.Menu) {
        return { content: <InsightMenuRow data={item} />, key: item.title };
      } else {
        return { content: item.content, key: item.key, type: item.type };
      }
    })}
  />
);

export const SummaryCard = ({
  children,
  className,
  link,
  title,
  tooltip,
  isError,
  isFetching,
  onRetry,
}: SummaryCardProps) => (
  <SummaryTableTemplate
    title={title}
    tooltip={tooltip}
    link={link}
    className={className}
    isError={isError}
    isFetching={isFetching}
    onRetry={onRetry}
    items={([] as React.ReactNode[]).concat(children ?? []).map((child: React.ReactNode) => ({
      content: child,
      key: 'summary-card',
    }))}
  />
);

export default SummaryTable;
